import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import { COUNTRIES, matchCountry } from '../../utils/countries';

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  placeholder?: string;
}

export function CountrySelect({
  value,
  onChange,
  label,
  className,
  placeholder = 'Select country',
}: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Resolve display name: if value is a code, show the name
  const displayName = useMemo(() => {
    if (!value) return '';
    const matched = matchCountry(value);
    return matched ? matched.name : value;
  }, [value]);

  const filtered = useMemo(() => {
    if (!search) return COUNTRIES;
    const lower = search.toLowerCase();
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(lower) || c.code.toLowerCase().includes(lower)
    );
  }, [search]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {label && (
        <label className="block text-sm font-medium text-dark-text-secondary mb-1">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setSearch('');
        }}
        className="w-full flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
      >
        <span className={value ? '' : 'text-dark-text-muted'}>{displayName || placeholder}</span>
        <ChevronDown className={clsx('w-4 h-4 ml-2 text-dark-text-secondary transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-dark-border">
            <Search className="w-3.5 h-3.5 text-dark-text-muted" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search countries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearch('');
                } else if (e.key === 'Enter' && filtered.length === 1) {
                  onChange(filtered[0].name);
                  setIsOpen(false);
                  setSearch('');
                }
              }}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {/* Clear option */}
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearch('');
              }}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm',
                !value ? 'text-dark-accent' : 'text-dark-text-muted italic hover:bg-dark-border'
              )}
            >
              No country
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-dark-text-muted text-center">
                No matching countries
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    onChange(c.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={clsx(
                    'w-full text-left px-3 py-2 text-sm',
                    value === c.name || displayName === c.name
                      ? 'bg-primary-900/30 text-primary-400'
                      : 'text-dark-text-secondary hover:bg-dark-border'
                  )}
                >
                  <span className="text-dark-text-muted mr-2">{c.code}</span>
                  {c.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
